import { Link } from 'react-router-dom'
import AppFooter from '../components/shared/AppFooter'

export default function TermsPage() {
  return (
    <div className="relative flex min-h-screen flex-col bg-base text-ink">
      <div className="mx-auto max-w-3xl px-4 py-16">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-ink/60 hover:text-ink">
          ← Back
        </Link>

        <h1 className="font-display text-3xl font-bold text-ink">
          Terms &amp; Conditions
        </h1>
        <p className="mt-2 text-sm text-amber/60">Last updated: March 18, 2026</p>

        <div className="mt-8 space-y-10">
          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">1. Agreement to Terms</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              By accessing or using Omore (“the Service”), operated by OMore (“we”, “us”, “our”),
              you agree to be bound by these Terms &amp; Conditions. If you do not agree, please do not use the Service.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              We encourage parents and guardians of users under 18 to review these terms along with our{' '}
              <Link to="/privacy" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/safety" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                Safety
              </Link>{' '}
              page.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              For information on how we handle your data, see our{' '}
              <Link to="/privacy" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                Privacy Policy
              </Link>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">2. The Service</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              Omore is a platform that lets users create and send digital postcards (“postys”)
              as visual shout-outs. Registered users can build personal and classroom landscapes by sending postys.
              The Service is free to use.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>No account required for core use:</strong> Up to 5 postys can be created and sent each day
              without signing up or providing any personal information.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>Registered accounts</strong> require only a username (3–20 lowercase letters and numbers)
              and a password. We do not collect email addresses or any other personal identifiers at sign-up.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">3. Accounts</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              Registered accounts give you access to landscape building, classroom features, and your posty history.
              You are responsible for keeping your login credentials confidential. Do not share your account with
              others or create accounts on behalf of someone else without their permission.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              You can permanently delete your account and all associated data at any time directly within the app.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">4. User Content</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              When you create a posty, you grant us a non-exclusive, worldwide, royalty-free licence to
              display and distribute your content within the Service — for example, in personal landscapes,
              classroom landscapes, shared links, and the featured landscapes gallery. You retain ownership of any
              original content you create.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              You agree not to create postys that contain:
            </p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
              <li>Hateful, threatening, or harassing content</li>
              <li>Sexually explicit or violent material</li>
              <li>Personal information about others without their consent</li>
              <li>Content that infringes on intellectual property rights</li>
              <li>Spam or commercial advertising</li>
            </ul>
            <p className="text-sm leading-relaxed text-ink/70">
              We moderate content and reserve the right to remove postys that violate these rules
              without notice.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>Posty share links expire automatically after 7 days.</strong>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">5. Classroom Feature</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              The classroom feature allows registered users to create shared spaces for groups. Classroom
              creators are responsible for managing their classroom and ensuring appropriate use by
              participants. Classrooms are limited to 40 members. Only registered users may create or join
              a classroom — guest users are not permitted in classrooms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">6. Acceptable Use</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              You agree not to:
            </p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorised access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated tools to scrape or collect data from the Service</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service to bully, harass, or intimidate others</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">7. Intellectual Property</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              The Service, including its design, branding, templates, stickers, and code, is owned by
              OMore and protected by intellectual property laws. You may not copy, modify, or distribute
              any part of the Service without our written permission.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">8. Disclaimer of Warranties</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              The Service is provided “as is” and “as available” without warranties of any kind, either
              express or implied. We do not guarantee that the Service will be uninterrupted, error-free,
              or secure.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">9. Limitation of Liability</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              To the maximum extent permitted by law, OMore shall not be liable for any indirect,
              incidental, special, or consequential damages arising from your use of the Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">10. Changes to Terms</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We may update these terms from time to time. If we make material changes, we will notify
              you through the Service. Your continued use of the Service after changes are posted
              constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">11. Contact</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              If you have questions about these terms, contact us at{' '}
              <a href="mailto:go@omore.app" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                go@omore.app
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex h-12 items-center rounded-2xl bg-amber px-6 font-marker text-base text-base shadow-glow-amber transition-all hover:brightness-105 active:scale-[0.97]"
          >
            Back to Home
          </Link>
        </div>
      </div>
      <AppFooter />
    </div>
  )
}
